import ButtonVariants from "@core-components/button/ButtonVariants";
import Icon from "@core-components/icon/Icon";
import useAppTranslation from "@hooks/language/useAppTranslation";
import { Group, Stack, Text, ThemeIcon } from "@mantine/core";
import type IOrder from "@modules/order/models/IOrder";
import {
  BOLD_TEXT_CLASS_NAME,
  LIGHT_TEXT_CLASS_NAME,
} from "@modules/order/pages/OrderTracking";
import { IconInfoCircleFilled } from "@tabler/icons-react";
import { useMemo } from "react";
import AccountCredential from "./AccountCrediential";
import SSDivider from "./Divider";
import SSSection from "./Section";

interface IAccountCredentialsProps {
  order: IOrder | undefined;
}

const AccountCredentials: React.FC<IAccountCredentialsProps> = ({ order }) => {
  const { t } = useAppTranslation("pages.order.tracking.credentials");
  const { t: tStatus } = useAppTranslation("pages.order");
  const isOrderNotPaid = useMemo(
    () => !order?.status.isPaid(),
    [order?.status],
  );

  return (
    <div className="relative">
      {isOrderNotPaid && (
        <Stack
          align="center"
          className="absolute z-10 h-full w-full"
          justify="center"
        >
          <Text className={`${BOLD_TEXT_CLASS_NAME} w-3/4 text-center text-sm`}>
            {order?.status.descriptionKey
              ? tStatus(order.status.descriptionKey)
              : order?.status.description}
          </Text>
          {order?.status.isPending() && (
            <ButtonVariants.Gradient
              className="hover-scale"
              label={t("payNow")}
              href={order?.stripeCheckoutSessionUrl}
              target="_blank"
            />
          )}
        </Stack>
      )}
      <SSSection
        borderColor="border-secret-blue-light"
        classNames={{
          content: `select-none ${isOrderNotPaid ? "blur-lg" : ""}`,
        }}
        color="bg-secret-blue-dark"
        icon={<Icon.PaymentSuccess size={20} />}
        title={t("title")}
      >
        <Stack className="w-full">
          <Stack className="max-h-[500px] overflow-y-auto">
            {order?.accounts?.map((item) => (
              <AccountCredential key={item.accountId} {...item} />
            ))}
          </Stack>
          <SSDivider />
          <Group align="center" className="pt-2 md:pt-6" gap={10} wrap="nowrap">
            <ThemeIcon size={"25px"} variant="transparent">
              <IconInfoCircleFilled color="white" />
            </ThemeIcon>
            <Text
              className={`${LIGHT_TEXT_CLASS_NAME} text-sm`}
              dangerouslySetInnerHTML={{ __html: t("copyInstructions") }}
            />
          </Group>
        </Stack>
      </SSSection>
    </div>
  );
};

export default AccountCredentials;
